import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { TastingStep, SpiritTasting, TastingSession } from '@/types';

interface TastingState {
  // Session data
  session: TastingSession | null;
  spirits: SpiritTasting[];
  currentSpiritIndex: number;
  currentStep: TastingStep;

  // UI state
  isLoading: boolean;
  error: string | null;

  // Actions
  setSession: (session: TastingSession | null) => void;
  setSpirits: (spirits: SpiritTasting[]) => void;
  addSpirit: (spirit: SpiritTasting) => void;
  updateSpirit: (index: number, updates: Partial<SpiritTasting>) => void;
  setCurrentSpiritIndex: (index: number) => void;
  setCurrentStep: (step: TastingStep) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;

  // Navigation helpers
  nextSpirit: () => void;
  previousSpirit: () => void;
  nextStep: () => void;
  previousStep: () => void;

  // Computed helpers
  getCurrentSpirit: () => SpiritTasting | null;
  isLastSpirit: () => boolean;
  isFirstSpirit: () => boolean;
  getProgress: () => number;
  isStepComplete: (step: TastingStep) => boolean;
}

const STEP_ORDER: TastingStep[] = ['intro', 'aroma', 'taste', 'finish', 'botanicals', 'summary'];

const initialState = {
  session: null,
  spirits: [],
  currentSpiritIndex: 0,
  currentStep: 'intro' as TastingStep,
  isLoading: false,
  error: null,
};

export const useTastingStore = create<TastingState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setSession: (session) => set({ session, currentStep: session?.current_step || 'intro' }),
      setSpirits: (spirits) => set({ spirits }),
      addSpirit: (spirit) => set((state) => ({ spirits: [...state.spirits, spirit] })),

      updateSpirit: (index, updates) => set((state) => ({
        spirits: state.spirits.map((s, i) =>
          i === index ? { ...s, ...updates } : s
        ),
      })),

      setCurrentSpiritIndex: (index) => set({ currentSpiritIndex: index }),
      setCurrentStep: (step) => set({ currentStep: step }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),

      reset: () => set(initialState),

      nextSpirit: () => {
        const { currentSpiritIndex, spirits } = get();
        if (currentSpiritIndex < spirits.length - 1) {
          set({ currentSpiritIndex: currentSpiritIndex + 1 });
        }
      },

      previousSpirit: () => {
        const { currentSpiritIndex } = get();
        if (currentSpiritIndex > 0) {
          set({ currentSpiritIndex: currentSpiritIndex - 1 });
        }
      },

      nextStep: () => {
        const { currentStep } = get();
        const currentIndex = STEP_ORDER.indexOf(currentStep);
        if (currentIndex < STEP_ORDER.length - 1) {
          set({ currentStep: STEP_ORDER[currentIndex + 1] });
        }
      },

      previousStep: () => {
        const { currentStep } = get();
        const currentIndex = STEP_ORDER.indexOf(currentStep);
        if (currentIndex > 0) {
          set({ currentStep: STEP_ORDER[currentIndex - 1] });
        }
      },

      getCurrentSpirit: () => {
        const { spirits, currentSpiritIndex } = get();
        return spirits[currentSpiritIndex] || null;
      },

      isLastSpirit: () => {
        const { spirits, currentSpiritIndex } = get();
        return currentSpiritIndex === spirits.length - 1;
      },

      isFirstSpirit: () => {
        const { currentSpiritIndex } = get();
        return currentSpiritIndex === 0;
      },

      getProgress: () => {
        const { currentStep, spirits, currentSpiritIndex } = get();
        if (spirits.length === 0) return 0;

        const stepIndex = STEP_ORDER.indexOf(currentStep);
        // Intro counts as 1/6, then each spirit contributes to the middle steps
        const stepsPerSpirit = (STEP_ORDER.length - 2) / spirits.length; // Exclude intro and summary
        const spiritProgress = currentSpiritIndex * stepsPerSpirit;

        if (currentStep === 'intro') return 5;
        if (currentStep === 'summary') return 95;

        const baseProgress = 10 + (spiritProgress * 80 / (STEP_ORDER.length - 2));
        const stepProgress = (stepIndex - 1) * (80 / ((STEP_ORDER.length - 2) * spirits.length));

        return Math.min(90, baseProgress + stepProgress);
      },

      isStepComplete: (step: TastingStep) => {
        const { spirits, currentSpiritIndex } = get();
        const spirit = spirits[currentSpiritIndex];
        if (!spirit) return false;

        switch (step) {
          case 'intro':
            return true; // Intro is always complete
          case 'aroma':
            return spirit.aroma_rating !== undefined;
          case 'taste':
            return spirit.taste_rating !== undefined;
          case 'finish':
            return spirit.finish_rating !== undefined;
          case 'botanicals':
            return (spirit.detected_botanicals?.length || 0) > 0;
          case 'summary':
            return spirit.overall_rating !== undefined;
          default:
            return false;
        }
      },
    }),
    {
      name: 'tasting-session-storage',
      partialize: (state) => ({
        session: state.session ? { uuid: state.session.uuid } : null,
        currentSpiritIndex: state.currentSpiritIndex,
        currentStep: state.currentStep,
      }),
    }
  )
);

export default useTastingStore;
